// Kiosk mode on the client (UC-PATIENTS-022; BR-PATIENTS-014; TEN-65; D62). Staff enter it from Today or a patient record:
// the reads the token will not reach are made first in the staff session (the title list, the patient pre-fill), then
// `POST /auth/handover` puts the session into handover and answers the single-use token. From then until the Control PIN exit
// the device shows the kiosk alone — the shell is not mounted, every other route redirects here (KioskConfinement) — and the
// kiosk's own idle timer discards the form, ends the handover locally and locks the screen (UC-PATIENTS-022 A2).
// The state is kept in sessionStorage so a reload mid-form stays in kiosk mode rather than showing the staff shell to whoever
// holds the tablet; it is removed on exit and on idle, so nothing the patient typed survives on the device.
//
// A session can be in handover while this tab holds no kiosk — the kiosk is open in another tab or window, or this tab's
// storage was cleared — and then every staff call answers `403 handover_mode`. Such a refusal is noted here ("stray handover")
// so the app can offer staff the Control PIN exit instead of a dead end (UC-PATIENTS-022 A7).
import { HandoverResponseSchema, SEEDED_PATIENT_TITLES, type HandoverForm, type HandoverFormType, type HandoverResponse, type PatientView, type SessionView } from "@visionx/contracts";
import { useSyncExternalStore } from "react";
import { newIdempotencyKey } from "../api/client";
import { handover, kioskPrefill, type PatientTitleOption } from "../api/endpoints.consent";
import type { Grant } from "../api/endpoints";
import { isApiError } from "../api/problem";
import { discardLocalReads, lockScreen } from "../session/store";

export interface KioskState {
  /** The single-use handover token; sent as X-Handover-Token on the submission and the consent calls. */
  token: string;
  expiresAt: string;
  formType: HandoverFormType;
  /** The handed-over patient; null for a new registration. */
  patientId: string | null;
  branchId: string;
  /** TEN-65: the organisation's handover idle minutes (default 10). */
  idleMinutes: number;
  /** The consent text version the kiosk presents; null when none is published. */
  consentTextVersion: number | null;
  /**
   * The handover answer's form (UC-PATIENTS-022 step 3): the practice's consent wording, the Owner's defaults, the consent text
   * and the privacy contact. A new patient's consent step shows exactly this. Absent from a state stored before it existed.
   */
  form?: HandoverForm | null | undefined;
  /** Where the staff member came from; the exit returns there. */
  returnTo: string;
  practiceName: string;
  branchName: string;
  titles: PatientTitleOption[];
  /** The existing patient's record for the pre-fill (Update, Consent, MedicalHistory); null for a new patient. */
  prefill: PatientView | null;
  enteredAt: string;
  /** Set once the submission is accepted: the token is spent and the kiosk shows the thank-you screen until the staff exit. */
  submissionId: string | null;
}

const KEY = "vx.kiosk";
/** Where the patient screens leave a handover they made themselves before navigating to /kiosk (agreed 22 Sep 2026). */
export const HANDOVER_KEY = "vx.handover";
/** Set when the idle timer ended kiosk mode: the unlock that follows returns to the staff shell instead of starting a new handover. */
const LEFT_KEY = "vx.kiosk.left";
let state: KioskState | null | undefined;
const listeners = new Set<() => void>();

function read(): KioskState | null {
  try { const raw = sessionStorage.getItem(KEY); return raw ? (JSON.parse(raw) as KioskState) : null; } catch { return null; }
}
function write(next: KioskState | null): void {
  try { if (next) sessionStorage.setItem(KEY, JSON.stringify(next)); else sessionStorage.removeItem(KEY); } catch { /* storage unavailable: the in-memory state still confines this tab */ }
}
function emit(next: KioskState | null): void {
  state = next;
  write(next);
  // A kiosk this tab holds is the session's handover: nothing stray remains.
  if (next !== null) stray = false;
  for (const l of listeners) l();
}

export function getKioskState(): KioskState | null {
  if (state === undefined) state = read();
  return state;
}

/** The practice and branch names the kiosk greets with ("Welcome to Dynamic Vision Menlyn"). */
export function kioskNamesOf(s: SessionView): { practiceName: string; branchName: string } {
  const branch = s.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? "";
  return { practiceName: s.organisation.tradingName ?? s.organisation.legalName, branchName: branch };
}

export interface EnterKioskOptions {
  formType: HandoverFormType;
  patientId?: string | undefined;
  /** The route to return to after the staff exit; defaults to the patient's record or Today. */
  returnTo?: string | undefined;
  session: SessionView;
}

/**
 * Enter kiosk mode: read the titles and the pre-fill in the staff session, then hand the session over. Throws the API's
 * problem (403 PERMISSION_DENIED, 404 NOT_FOUND, 409 ILLEGAL_TRANSITION `already_in_handover`) without changing anything.
 */
export async function enterKiosk(opts: EnterKioskOptions): Promise<KioskState> {
  try {
    const titles = await kioskPrefill.titles();
    const prefill = opts.patientId ? (await kioskPrefill.patient(opts.patientId)).data : null;
    const body = { formType: opts.formType, ...(opts.patientId ? { patientId: opts.patientId } : {}) };
    const { data } = await handover.enter(body as never, newIdempotencyKey());
    const next = stateOf(data, opts.session, { returnTo: opts.returnTo ?? (opts.patientId ? `/patients/${opts.patientId}` : "/today"), titles, prefill });
    try { sessionStorage.removeItem(LEFT_KEY); } catch { /* storage unavailable */ }
    emit(next);
    // Nothing the staff screens read stays on the device in patient mode: neither the query cache nor the service worker's copy
    // (review, 5 Oct 2026: a Back press drew the staff page the device came from, other patients' names in it).
    void discardLocalReads();
    return next;
  } catch (err) {
    // The session is already handed over (another tab or window, or storage cleared): the app offers the PIN exit.
    if (isHandoverRefusal(err)) noteStrayHandover();
    throw err;
  }
}

function stateOf(data: HandoverResponse, session: SessionView, extra: { returnTo: string; titles: PatientTitleOption[]; prefill: PatientView | null }): KioskState {
  const form = data.form ?? null;
  return {
    token: data.handoverToken, expiresAt: data.expiresAt, formType: data.formType, patientId: data.patientId, branchId: data.branchId,
    idleMinutes: data.idleMinutes, consentTextVersion: data.consentTextVersion, form,
    returnTo: extra.returnTo, ...kioskNamesOf(session),
    // The answer's active titles when it carries them; otherwise the list read before the handover (or the seeded five).
    titles: form && form.titles.length > 0 ? form.titles : extra.titles,
    prefill: extra.prefill, enteredAt: new Date().toISOString(), submissionId: null,
  };
}

/** A handover another screen already made and left in router state or `sessionStorage["vx.handover"]`, validated; null when there is none. */
export function pendingHandover(routerState: unknown): HandoverResponse | null {
  const fromState = routerState && typeof routerState === "object" && "handover" in routerState ? (routerState as { handover: unknown }).handover : undefined;
  let stored: unknown;
  try { const raw = sessionStorage.getItem(HANDOVER_KEY); stored = raw ? JSON.parse(raw) : undefined; } catch { stored = undefined; }
  for (const candidate of [fromState, stored]) {
    const parsed = HandoverResponseSchema.safeParse(candidate);
    if (parsed.success) return parsed.data;
  }
  return null;
}

/**
 * Adopt a handover another screen made (the patient record's "Re-consent on kiosk"): the session is already in handover on the
 * server, so nothing is posted and the reads the token does not reach are not attempted — the answer's titles (else the seeded
 * five) stand in and there is no pre-fill. The stored copy is consumed so it is not adopted twice.
 */
export function adoptHandover(data: HandoverResponse, session: SessionView, extra: { returnTo?: string | undefined } = {}): KioskState {
  const next = stateOf(data, session, { returnTo: extra.returnTo ?? (data.patientId ? `/patients/${data.patientId}` : "/today"), titles: SEEDED_PATIENT_TITLES.map((t) => ({ code: t.code, label: t.label })), prefill: null });
  try { sessionStorage.removeItem(HANDOVER_KEY); sessionStorage.removeItem(LEFT_KEY); } catch { /* storage unavailable */ }
  emit(next);
  void discardLocalReads();
  return next;
}

/** True once, after the idle timer ended kiosk mode: the /kiosk route then returns to the staff shell rather than starting afresh. */
export function consumeKioskLeft(): boolean {
  try { const left = sessionStorage.getItem(LEFT_KEY) !== null; sessionStorage.removeItem(LEFT_KEY); return left; } catch { return false; }
}

/** Leave kiosk mode on this device only — after the API's exit, or when the idle timer fires. */
export function endKioskLocally(): void {
  endedAt = Date.now();
  emit(null);
}

/**
 * The session ended (sign-out, "Switch user" on the lock screen, a 401 for a revoked or expired session): nothing of the kiosk
 * may outlive it on the device — not the confinement, which would send sign-in back to /kiosk, nor the handed-over patient's
 * pre-fill in sessionStorage, nor a handover waiting to be adopted or the idle note.
 */
export function clearKioskDevice(): void {
  try { sessionStorage.removeItem(HANDOVER_KEY); sessionStorage.removeItem(LEFT_KEY); } catch { /* storage unavailable */ }
  if (getKioskState() !== null) emit(null);
}

/**
 * The Control PIN exit: `DELETE /auth/handover` with the step-up token the modal obtained (UC-PATIENTS-022 step 6) and the
 * Idempotency-Key the route requires (BR-PATIENTS-021). The caller mints the key once per exit attempt, outside the step-up
 * retry, so the refused first call and the retry that carries the token send the same key. Answers the exit so a caller can
 * tell it from a cancelled prompt.
 */
export async function exitKiosk(grant: Grant, idempotencyKey: string): Promise<{ exitedAt: string; submissionId: string | null; locked?: true }> {
  try {
    const { data } = await handover.exit({ ...grant, idempotencyKey });
    endKioskLocally();
    return { exitedAt: data.exitedAt, submissionId: data.submissionId };
  } catch (err) {
    if (notInHandover(err)) {
      // UC-PATIENTS-022 A8: the handover already ended on the server — an exit from another tab, or its idle expiry — and the
      // request was refused before any PIN was asked for. A kiosk on this device is in a patient's hands: it locks, and only
      // a staff unlock leads on, to the staff shell (review, 4 Oct 2026: the exit had opened the staff screens with no PIN).
      // Without one (the stray-handover notice, A7.3) staff are in front of the screen and there is nothing left to leave.
      if (getKioskState() !== null) {
        await abandonKiosk();
        return { exitedAt: new Date().toISOString(), submissionId: null, locked: true };
      }
      endKioskLocally();
      return { exitedAt: new Date().toISOString(), submissionId: null };
    }
    throw err;
  }
}

/** `409 ILLEGAL_TRANSITION` with `meta.reason 'not_in_handover'`: the session is not in handover on the server. */
export function notInHandover(err: unknown): boolean {
  return isApiError(err) && err.code === "ILLEGAL_TRANSITION" && err.meta.reason === "not_in_handover";
}

/**
 * The idle limit passed (UC-PATIENTS-022 A2): the screen locks first, so the lock screen — never the staff shell — is what the
 * device shows next, and only then is the handover over on this device; the form state dies with the kiosk component.
 */
export async function abandonKiosk(): Promise<void> {
  await lockScreen();
  leftLocked();
}

/**
 * Patient mode ends on this device after a lock: the unlock that follows returns to the staff shell (UC-PATIENTS-022 A2.1)
 * instead of starting a new handover.
 */
export function leftLocked(): void {
  try { sessionStorage.setItem(LEFT_KEY, "1"); } catch { /* storage unavailable */ }
  endKioskLocally();
}

/** The handover token was spent by the submission (or the consent-only capture); the kiosk stays confined until the staff exit. */
export function markKioskSubmitted(submissionId: string): void {
  const cur = getKioskState();
  if (cur) emit({ ...cur, token: "", submissionId });
}

export function useKiosk(): KioskState | null {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, getKioskState, () => null);
}

// --- A handover this tab does not hold (UC-PATIENTS-022 A7) ------------------------------------------------------------------

/** When this tab last ended a kiosk (ms since the epoch): a session view read before then may still name that handover. */
let endedAt = 0;
let stray = false;

/**
 * The API says the session is in kiosk mode: `403 PERMISSION_DENIED` with `meta.reason 'handover_mode'` for a staff route, or
 * `409 ILLEGAL_TRANSITION` with `meta.reason 'already_in_handover'` for a second entry.
 */
export function isHandoverRefusal(err: unknown): boolean {
  return isApiError(err) && ((err.code === "PERMISSION_DENIED" && err.meta.reason === "handover_mode") || (err.code === "ILLEGAL_TRANSITION" && err.meta.reason === "already_in_handover"));
}

/** A handover refusal reached this tab. Ignored while this tab holds the kiosk: that handover is its own. */
export function noteStrayHandover(): void {
  if (getKioskState() !== null || stray) return;
  stray = true;
  for (const l of listeners) l();
}

/** The stray handover was left (or turned out to be over already), or staff put the notice away. */
export function clearStrayHandover(): void {
  if (!stray) return;
  stray = false;
  for (const l of listeners) l();
}

/** Whether the session view's `handover` block may name a handover this tab has not already ended itself. */
export function handoverIsLive(enteredAt: string): boolean {
  return Date.parse(enteredAt) > endedAt;
}

const strayNow = (): boolean => stray && getKioskState() === null;
/** True while a handover refusal stands with no kiosk in this tab. */
export function useStrayHandover(): boolean {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; }, strayNow, () => false);
}

/** Test seam: replace the kiosk state in place. */
export function setKioskState(next: KioskState | null): void { emit(next); }
/** Test seam: forget the in-memory state so the next read comes from sessionStorage, as after a reload; the stray-handover note goes too. */
export function resetKioskStore(): void { state = undefined; stray = false; endedAt = 0; }
